import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Form, Button, Card, Container, Row, Col, Alert } from 'react-bootstrap';
import { useAuth } from '../hooks/useAuth';
import { FiUserPlus, FiMail, FiLock, FiBriefcase } from 'react-icons/fi';

const SignupPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
    businessName: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.passwordConfirm) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      const result = await signup({
        email: formData.email,
        password: formData.password,
        passwordConfirm: formData.passwordConfirm,
        businessName: formData.businessName
      });

      if (result.success) {
        // Show success message about email verification
        setError('');
        setSuccess(result.message || 'Account created successfully! Please check your email to verify your account.');
        setFormData({
          email: '',
          password: '',
          passwordConfirm: '',
          businessName: ''
        });
      } else {
        setError(result.message || 'Signup failed');
        setSuccess('');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during signup');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="mt-5 mb-5">
      <Row className="justify-content-center">
        <Col md={6} lg={5}>
          <Card className="shadow-lg border-0">
            <Card.Header className="text-center py-4">
              <div className="mb-3">
                <FiUserPlus style={{ fontSize: '3rem', color: 'white' }} />
              </div>
              <Card.Title className="mb-0" style={{ color: 'white', fontSize: '1.75rem' }}>
                Create Your Account
              </Card.Title>
              <p className="mb-0 mt-2" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                Start managing your appointments today
              </p>
            </Card.Header>
            <Card.Body className="p-4">
              {error && (
                <Alert variant="danger" className="mb-4">
                  {error}
                </Alert>
              )}

              {success && (
                <Alert variant="success" className="mb-4">
                  {success}
                  <div className="mt-2">
                    <Link to="/login">Go to Login</Link>
                  </div>
                </Alert>
              )}

              {!success && (
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-4" controlId="businessName">
                  <Form.Label>
                    <FiBriefcase className="me-2" />
                    Business Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="businessName"
                    placeholder="Enter your business name"
                    value={formData.businessName}
                    onChange={handleChange}
                    required
                    className="form-control-lg"
                  />
                </Form.Group>

                <Form.Group className="mb-4" controlId="email">
                  <Form.Label>
                    <FiMail className="me-2" />
                    Email Address
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-control-lg"
                  />
                </Form.Group>

                <Form.Group className="mb-4" controlId="password">
                  <Form.Label>
                    <FiLock className="me-2" />
                    Password
                  </Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    placeholder="Create a password (min. 8 characters)"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={8}
                    className="form-control-lg"
                  />
                </Form.Group>

                <Form.Group className="mb-4" controlId="passwordConfirm">
                  <Form.Label>
                    <FiLock className="me-2" />
                    Confirm Password
                  </Form.Label>
                  <Form.Control
                    type="password"
                    name="passwordConfirm"
                    placeholder="Confirm your password"
                    value={formData.passwordConfirm}
                    onChange={handleChange}
                    required
                    className="form-control-lg"
                  />
                </Form.Group>

                <Button 
                  variant="primary" 
                  type="submit" 
                  className="w-100 py-3 mb-3" 
                  disabled={loading}
                  style={{ fontSize: '1.1rem', fontWeight: 600 }}
                >
                  {loading ? (
                    <>
                      <span className="loading-spinner me-2"></span>
                      Creating account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </Form>
              )}

              <div className="text-center mt-4">
                <p className="text-muted mb-2">Already have an account?</p>
                <Link to="/login" style={{ textDecoration: 'none', fontWeight: 600 }}>
                  Sign in instead
                </Link>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SignupPage;
